/* iter-72o — UpgradeManager
 *
 * Consolidates the two independent upgrade-adjacent dialog hosts that
 * previously lived side-by-side in `App.js`:
 *   • Demo → Pro upgrade modal (raised by DemoContext).
 *   • Trial → Pro upgrade dialog (raised by useAuth + custom event).
 *
 * TrialBanner keeps living inside AppShell because it is a structural
 * banner (participates in the top-of-shell stack) rather than a modal.
 *
 * Keeping the hosts together makes it easy to reason about upgrade
 * flow ordering: the demo modal always wins if both fire at once,
 * because it renders LAST and shares the same z-index.
 */
import React, { useEffect, useState } from 'react';
import DemoUpgradeModal from './DemoUpgradeModal';
import TrialUpgradeDialog from './TrialUpgradeDialog';
import { useDemo } from '@/lib/demo_context';
import { useAuth } from '@/lib/auth_context';
import { Button } from '@/components/ui/button';

function PromoExpiredDialog() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user?.promo_expired_at) return;
    const key = `promo_expired_dismissed_${user.user_id}`;
    if (!sessionStorage.getItem(key)) setOpen(true);
  }, [user]);

  if (!open) return null;

  function dismiss() {
    const key = `promo_expired_dismissed_${user.user_id}`;
    sessionStorage.setItem(key, '1');
    setOpen(false);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div
        data-testid="promo-expired-dialog"
        className="w-full max-w-md rounded-2xl bg-zinc-900 border border-zinc-700/60 shadow-2xl p-6 space-y-4"
      >
        <div className="space-y-1">
          <h2 className="text-base font-semibold text-white">Your free chapters are saved</h2>
          <p className="text-sm text-zinc-400">
            The Writer's Launch promotion has ended. Your draft is still here — upgrade to Pro
            to keep writing, unlock unlimited chapters, and publish to the Bookstore.
          </p>
        </div>
        <div className="flex gap-3">
          <Button
            data-testid="promo-expired-upgrade-btn"
            className="flex-1 bg-violet-600 hover:bg-violet-500 text-white"
            onClick={() => { dismiss(); window.dispatchEvent(new CustomEvent('open-upgrade-modal', { detail: { source: 'promo_expired' } })); }}
          >
            Upgrade to Pro
          </Button>
          <Button variant="ghost" onClick={dismiss} className="text-zinc-400">
            Later
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function UpgradeManager() {
  const { modalOpen, modalSource, closeUpgrade } = useDemo();
  return (
    <>
      <TrialUpgradeDialog />
      <PromoExpiredDialog />
      <DemoUpgradeModal open={modalOpen} source={modalSource} onClose={closeUpgrade} />
    </>
  );
}
